import React from "react";
import {
FiSearch,
FiChevronDown,
FiMoreVertical,
FiChevronLeft,
FiChevronRight,
FiFilter,
} from "react-icons/fi";

import {
HiOutlineExclamationTriangle,
HiOutlineShieldExclamation,
} from "react-icons/hi2";

import { useIncident } from "../context/incidentContext";
import { incidentAge, isOpen } from "../incidentData";

// ============================================================
// HELPER: FORMAT ENUM VALUES
// ============================================================

const formatLabel = (value) => {
if (!value) return "—";

return String(value)
.replaceAll("_", " ")
.toLowerCase()
.replace(/\b\w/g, (char) => char.toUpperCase());
};

// ============================================================
// HELPER: GET TYPE VARIANT
// ============================================================

const getTypeVariant = (type) => {
switch (type) {
case "CUSTOMER_INJURY":
case "STAFF_INJURY":
case "FIRE_SAFETY":
case "SECURITY_INCIDENT":
return "danger";

case "FOOD_SPILL":
case "ALLERGEN_DIETARY":
case "EQUIPMENT_FAILURE":
  return "warning";

default:
  return "info";

}
};

// ============================================================
// HELPER: FORMAT USER NAME
// ============================================================

const getUserName = (user) => {
if (!user) return "—";

if (typeof user === "string") {
return user;
}

return user.fullName || "—";
};

// ============================================================
// COMPONENT
// ============================================================

export default function IncidentLogTable() {
const { incidents, pageIncidents, records, filters, updateFilter, filtersOpen, setFiltersOpen, loading, emptyMessage, currentPage, totalPages, pageLimit, setPage, setPanel, today } = useIncident();

// ============================================================
// MAP API INCIDENTS TO TABLE DATA
// ============================================================

const incidentsData = pageIncidents.map((incident) => {
const incidentDate = incident.incidentDateTime
? new Date(incident.incidentDateTime)
: null;

const isValidDate =
  incidentDate &&
  !Number.isNaN(incidentDate.getTime());

const daysOpen = isOpen(incident) ? incidentAge(incident, today) : null;

return {
  id: incident.id,

  // ========================================================
  // INCIDENT TITLE
  // ========================================================

  title:
    incident.description ||
    formatLabel(incident.type) ||
    "Untitled Incident",

  // ========================================================
  // INCIDENT TYPE
  // ========================================================

  type: {
    label: formatLabel(incident.type),
    variant: getTypeVariant(incident.type),
  },

  // ========================================================
  // LOCATION
  // ========================================================

  complianceArea: incident.location || "—",

  // ========================================================
  // DATE & TIME
  // ========================================================

  reported: {
    date: isValidDate
      ? incidentDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—",

    time: isValidDate
      ? incidentDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—",
  },

  // ========================================================
  // USERS
  // ========================================================

  reportedBy: getUserName(incident.createdBy),

  assignedTo: getUserName(incident.referredTo),

  // ========================================================
  // SEVERITY
  // Prisma currently does not have a severity field.
  // Impact is descriptive text, not an incident severity classification.
  // ========================================================

  severity: "Not tracked",

  // ========================================================
  // STATUS
  // ========================================================

  status: formatLabel(incident.status || "OPEN"),

  rawStatus: incident.status || "OPEN",

  // ========================================================
  // DAYS OPEN
  // ========================================================

  daysOpen,
};

});

// ============================================================
// TYPE BADGE
// ============================================================

const TypeBadge = ({ type }) => {
const iconClass = "w-3.5 h-3.5";
let containerClass =
"inline-flex items-center gap-1 text-xs font-semibold ";


if (type.variant === "danger") {
  containerClass += "text-rose-600";

  return (
    <span className={containerClass}>
      <HiOutlineExclamationTriangle
        className={iconClass}
      />
      {type.label}
    </span>
  );
}

if (type.variant === "warning") {
  containerClass += "text-amber-500";

  return (
    <span className={containerClass}>
      <HiOutlineShieldExclamation
        className={iconClass}
      />
      {type.label}
    </span>
  );
}

containerClass += "text-indigo-600";

return (
  <span className={containerClass}>
    <div className="w-3.5 h-3.5 rounded border border-indigo-600/80 flex items-center justify-center text-[9px] font-extrabold">
      !
    </div>

    {type.label}
  </span>
);

};

// ============================================================
// SEVERITY STYLE
// ============================================================

const getSeverityStyle = (severity) => {
const value = String(severity).toLowerCase();

switch (value) {
  case "high":
  case "critical":
    return "text-rose-600 font-bold bg-rose-50/50 px-1.5 py-0.5 rounded";

  case "medium":
    return "text-amber-600 font-bold bg-amber-50/50 px-1.5 py-0.5 rounded";

  case "low":
    return "text-emerald-600 font-semibold";

  default:
    return "text-slate-600";
}

};

// ============================================================
// STATUS STYLE
// ============================================================

const getStatusStyle = (status) => {
switch (status) {
case "OPEN":
return "bg-amber-50 text-amber-600 border border-amber-100";

  case "IN_PROGRESS":
    return "bg-blue-50 text-blue-600 border border-blue-100";

  case "COMPLETED":
    return "bg-emerald-50 text-emerald-600 border border-emerald-100";

  default:
    return "bg-slate-50 text-slate-600 border border-slate-200";
}

};

// ============================================================
// RENDER
// ============================================================

return ( <div id="incident-inventory" className="w-full bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden font-sans">

  {/* Header */}
  <div className="p-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100">

    <div>
      <h2 className="text-sm font-bold text-slate-900 tracking-tight">
        All Incidents
      </h2>
    </div>

    {/* Filters */}
    <div className="flex flex-wrap items-center gap-2.5">

      {/* Search */}
      <div className="relative min-w-[200px]">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />

        <input
          type="text"
          aria-label="Search incidents"
          value={filters.search}
          onChange={event => updateFilter("search", event.target.value)}
          placeholder="Search incidents..."
          className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs placeholder-slate-400 text-slate-700 focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Status */}
      <div className="relative">
        <select aria-label="Incident status" value={filters.status} onChange={event => updateFilter("status", event.target.value)} className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 focus:outline-none min-w-[110px]">
          <option value="">All Statuses</option>
          <option value="ACTIVE">All Unresolved</option>
          <option value="OPEN">Open</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
        </select>

        <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>

      {/* Type */}
      <div className="relative">
        <select aria-label="Incident type" value={filters.type} onChange={event => updateFilter("type", event.target.value)} className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 focus:outline-none min-w-[100px]">
          <option value="">All Types</option>

          <option value="CUSTOMER_INJURY">Customer Injury</option>
          <option value="STAFF_INJURY">Staff Injury</option>
          <option value="FOOD_SPILL">Food Spill</option>
          <option value="EQUIPMENT_FAILURE">Equipment Failure</option>
          <option value="PROPERTY_DAMAGE">Property Damage</option>
          <option value="SECURITY_INCIDENT">Security Incident</option>
          <option value="ALLERGEN_DIETARY">Allergen Dietary</option>
          <option value="CUSTOMER_COMPLAINT">Customer Complaint</option>
          <option value="FIRE_SAFETY">Fire Safety</option>
          <option value="OTHER">Other</option>
        </select>

        <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>

      {/* Areas */}
      <div className="relative">
        <select aria-label="Incident location" value={filters.location} onChange={event => updateFilter("location", event.target.value)} className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 focus:outline-none min-w-[100px]">
          <option value="">All Areas</option>
          {[...new Set(records.map(row => row.location).filter(Boolean))].sort().map(location => <option key={location}>{location}</option>)}
        </select>

        <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>

      {/* More Filters */}
      <button type="button" onClick={() => setFiltersOpen(!filtersOpen)} aria-expanded={filtersOpen} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 transition">
        <FiFilter className="w-3.5 h-3.5 text-slate-400" />
        <span>More Filters</span>
      </button>
    </div>
  </div>

  {/* Table */}
  <div className="w-full overflow-x-auto">
    <table className="w-full border-collapse text-left min-w-[1100px]">

      <thead>
        <tr className="border-b border-slate-100 bg-slate-50/40 text-[11px] font-bold text-slate-400 tracking-wider">
          <th className="py-3 px-4 w-[110px]">ID</th>
          <th className="py-3 px-4 w-[200px]">Incident Title</th>
          <th className="py-3 px-4 w-[150px]">Type</th>
          <th className="py-3 px-4 w-[180px]">Location</th>
          <th className="py-3 px-4 w-[120px]">Reported</th>
          <th className="py-3 px-4 w-[110px]">Reported By</th>
          <th className="py-3 px-4 w-[110px]">Assigned To</th>
          <th className="py-3 px-4 w-[80px]">Severity</th>
          <th className="py-3 px-4 w-[140px]">Status</th>
          <th className="py-3 px-4 w-[90px] text-center">
            Days Open
          </th>
          <th className="py-3 px-4 w-[60px] text-center">
            Actions
          </th>
        </tr>
      </thead>

      <tbody className="divide-y divide-slate-100 text-xs">

        {incidentsData.map((row) => (
          <tr
            key={row.id}
            className="hover:bg-slate-50/50 transition-colors"
          >
            {/* ID */}
            <td className="py-3.5 px-4 font-bold text-blue-600 whitespace-nowrap">
              <button type="button" onClick={() => setPanel({ kind: "details", id: row.id })} className="font-bold text-blue-600">INC-{String(row.id).padStart(4, "0")}</button>
            </td>

            {/* Title */}
            <td className="py-3.5 px-4 font-bold text-slate-800 max-w-[250px]">
              <span className="line-clamp-2">
                {row.title}
              </span>
            </td>

            {/* Type */}
            <td className="py-3.5 px-4 whitespace-nowrap">
              <TypeBadge type={row.type} />
            </td>

            {/* Location */}
            <td className="py-3.5 px-4 font-semibold text-slate-600 whitespace-nowrap">
              {row.complianceArea}
            </td>

            {/* Reported */}
            <td className="py-3.5 px-4">
              <div className="flex flex-col min-w-0">
                <span className="font-bold text-slate-800 whitespace-nowrap">
                  {row.reported.date}
                </span>

                <span className="text-[10px] text-slate-400 font-medium tracking-tight mt-0.5 whitespace-nowrap">
                  {row.reported.time}
                </span>
              </div>
            </td>

            {/* Reported By */}
            <td className="py-3.5 px-4 text-slate-600 font-semibold whitespace-nowrap">
              {row.reportedBy}
            </td>

            {/* Assigned To */}
            <td className="py-3.5 px-4 text-slate-600 font-semibold whitespace-nowrap">
              {row.assignedTo}
            </td>

            {/* Severity */}
            <td className="py-3.5 px-4">
              <span className={getSeverityStyle(row.severity)}>
                {row.severity}
              </span>
            </td>

            {/* Status */}
            <td className="py-3.5 px-4">
              <span
                className={`inline-block px-2.5 py-0.5 text-[10px] font-bold rounded-full whitespace-nowrap ${getStatusStyle(
                  row.rawStatus,
                )}`}
              >
                {row.status}
              </span>
            </td>

            {/* Days Open */}
            <td className="py-3.5 px-4 text-center font-bold text-slate-800">
              {row.daysOpen ?? "—"}
            </td>

            {/* Actions */}
            <td className="py-3.5 px-4 text-center">
              <button type="button" aria-label={"View incident INC-" + String(row.id).padStart(4, "0")} onClick={() => setPanel({ kind: "details", id: row.id })} className="text-slate-400 hover:text-slate-600 p-1 rounded transition">
                <FiMoreVertical className="w-4 h-4 mx-auto" />
              </button>
            </td>
          </tr>
        ))}

        {/* Empty State */}
        {!incidentsData.length && (
          <tr>
            <td
              colSpan="11"
              className="py-12 text-center text-sm text-slate-400"
            >
              {emptyMessage("No incidents found in this range or filters.")}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>

  {/* Footer */}
  <div className="px-4 py-3 bg-white border-t border-slate-100 flex items-center justify-between">

    <span className="text-xs font-semibold text-slate-400 tracking-wide">
      Showing {incidents.length ? (currentPage - 1) * pageLimit + 1 : 0} to {Math.min(currentPage * pageLimit, incidents.length)} of{" "}
      {incidents.length} incidents
    </span>

    <div className="flex items-center gap-1.5">

      <button
        className="p-1.5 border border-slate-200 rounded-lg text-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 transition"
        type="button"
        aria-label="Previous incidents page"
        disabled={currentPage <= 1 || loading}
        onClick={() => setPage(currentPage - 1)}
      >
        <FiChevronLeft className="w-4 h-4" />
      </button>

      <button type="button" aria-label={"Current incidents page " + currentPage} className="w-6 h-6 flex items-center justify-center rounded-md text-xs font-bold bg-blue-600 text-white shadow-sm">
        {currentPage}
      </button>

      <button
        className="p-1.5 border border-slate-200 rounded-lg text-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 transition"
        type="button"
        aria-label="Next incidents page"
        disabled={currentPage >= totalPages || loading}
        onClick={() => setPage(currentPage + 1)}
      >
        <FiChevronRight className="w-4 h-4" />
      </button>
    </div>
  </div>
</div>

);
}
